import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | success | failed | expired
  const [message, setMessage] = useState("");
  const [attempts, setAttempts] = useState(0);

  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      setMessage("No payment session found.");
      return;
    }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async (attempt = 0) => {
    if (attempt >= 6) {
      setStatus("failed");
      setMessage("Payment verification timed out. Please check your account.");
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      const { payment_status, status: txStatus } = res.data;

      if (payment_status === "paid") {
        await refreshUser();
        setStatus("success");
        setMessage("Your payment was successful! Your account has been updated.");
      } else if (txStatus === "expired") {
        setStatus("expired");
        setMessage("Your payment session expired. Please try again.");
      } else {
        setAttempts(attempt + 1);
        setTimeout(() => pollStatus(attempt + 1), 2000);
      }
    } catch {
      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(attempt + 1), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center max-w-md w-full">
        {status === "loading" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Verifying Payment</h2>
            <p className="text-slate-500 text-sm">Please wait while we confirm your payment...</p>
            <p className="text-slate-400 text-xs mt-2">Attempt {attempts + 1}/6</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Payment Successful!</h2>
            <p className="text-slate-600 text-sm mb-6">{message}</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-blue-600 hover:bg-blue-700 text-white px-8" data-testid="go-to-dashboard-btn">
              Go to Dashboard
            </Button>
          </>
        )}
        {(status === "failed" || status === "expired") && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {status === "expired" ? "Session Expired" : "Payment Issue"}
            </h2>
            <p className="text-slate-600 text-sm mb-6">{message}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate("/pricing")} data-testid="retry-payment-btn">
                Try Again
              </Button>
              <Button onClick={() => navigate("/dashboard")} className="bg-blue-600 hover:bg-blue-700 text-white">
                Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
